import React, { useCallback, useEffect, useRef, useState } from "react";
import client, { makePatch } from "@culturehq/client";
import { Button, Icon, ModalDialog, Pagination, PlainButton, Table } from "@culturehq/components";
import { useDoor } from "app/hooks";
import { errorService, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ChqModalDialog from "components/common/ChqModalDialog";
import ProfileSearchBar from "../ProfileSearchBar";
import UserRow from "./UserRow";

const SelectUserModal = ({
  initialOpen = false, onStoryUpdate, activeStory, user
}) => {
  const isMounted = useRef(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [usersState, setUsersState] = useState([]);
  const [pagination, setPagination] = useState({});
  const [selectedUser] = useState(user);
  const [filter, setFilter] = useState(undefined);
  const { open, onOpen, onClose } = useDoor(initialOpen);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getUsersWithSelectedValue = useCallback(
    users => {
      if (!selectedUser) {
        return users;
      }

      return (
        selectedUser
          ? users.map(user => (
            { ...user, selected: selectedUser.id === user.id }
          )) : users
      );
    }, [selectedUser]
  );

  const fetchUsers = useCallback(async (pageNumber = page, activeFilter = filter) => {
    setLoading(true);

    try {
      if (activeFilter) {
        setLoading(false);
        return;
      }

      const { users, pagination } = await client.listUsers({ page: pageNumber });
      if (!isMounted.current) return;

      setUsersState(getUsersWithSelectedValue(users));
      setPagination(pagination);
      setLoading(false);
    } catch (error) {
      errorService.error(error);
      setLoading(false);
    }
  }, [getUsersWithSelectedValue, page, filter]);

  useEffect(() => {
    if (selectedUser) {
      if (!filter) {
        fetchUsers();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedUser, filter]);

  const handlePageSelected = pageNumber => {
    setPage(pageNumber);
    if (!filter) {
      fetchUsers(pageNumber);
    }
  };

  const handleResults = ({ users, pagination, query }) => {
    setFilter(query);
    if (!query) {
      setPage(1);
      fetchUsers(1, false);
    } else {
      setUsersState(getUsersWithSelectedValue(users));
      setPagination(pagination);
    }
  };

  const handleSelected = async newSelectedUser => {
    setUsersState(
      usersState.map(user => (
        { ...user, selected: newSelectedUser.id === user.id }
      ))
    );
  };

  const findSelectedUser = async () => {
    const selectedUserNew = usersState.find(user => user.selected === true);
    const { story, userActivity } = await makePatch(`/stories/update_creator/${activeStory.id}`, { creatorId: selectedUserNew.id });
    onStoryUpdate(story, userActivity);
    onClose();
  };

  const handleUnselected = async _changedUser => {};

  const onCloseModal = () => {
    setFilter(undefined);
    setPage(1);
    onClose();
  };

  return (
    <>
      {open && (
        <ChqModalDialog onClose={onCloseModal} className="SelectUser" entrance="zoomIn" ariaLabelledBy="select-user-title" ariaDescribedby="select-user-title">
          <ModalDialog.Heading titleId="select-user-title" onClose={onCloseModal}>
            <Trans>Select User</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="row">
              <div className="col-xs-12">
                <ProfileSearchBar
                  onResults={handleResults}
                  page={page}
                />
              </div>
              <div className="col-xs-12">
                <ErrorBoundary>
                  <Table className="no-scrollbar">
                    <thead>
                      <tr>
                        <th />
                        <th><Trans>Name</Trans></th>
                        <th className="actions">
                          <Trans>Actions</Trans>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersState.map(user => (
                        <UserRow
                          key={user.id}
                          onSelected={handleSelected}
                          onUnselected={handleUnselected}
                          user={user}
                        />
                      ))}
                    </tbody>
                  </Table>
                </ErrorBoundary>
              </div>
              <div className="col-xs-12">
                <Pagination {...pagination} onClick={handlePageSelected} />
              </div>
            </div>
            <div className="SelectUser-Btn">
              <Button
                className="save-btn"
                onClick={findSelectedUser}
              >
                <Trans>Save</Trans>
              </Button>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <PlainButton className="SelectUserBtn" onClick={onOpen}>
        <Icon icon="edit" />
        <Trans> </Trans>
      </PlainButton>
    </>
  );
};

export default SelectUserModal;
